import type { V2 } from '../core/math';
import { Actor } from './actor';
import { type PassiveRef, passiveHooks } from './passives';
import { registry } from './registry';
import { resolveSkill } from './skills';
import { type BaseStats, defaultBaseStats } from './stats';
import type { EntityId, PeerId, Team } from './types';
import { GRADE_RANK, isBossGrade } from './types';
import type { World } from './world';

export interface PlayerSpec {
  id?: EntityId;
  owner: PeerId;
  name: string;
  classId: string;
  level: number;
  pos: V2;
  team?: Team;
  /** Final base stats (class + level + gear), computed by the game layer. */
  base?: Partial<BaseStats>;
  bar?: (string | null)[];
  awakening?: string | null;
  skillLevels?: Record<string, number>;
  tripods?: Record<string, (string | null)[]>;
  look?: Actor['look'];
  passives?: PassiveRef[];
  skillMods?: Record<string, { dmg?: number; cd?: number }>;
  awakeningUses?: number;
}

export function createPlayer(world: World, spec: PlayerSpec): Actor {
  const cls = registry.cls(spec.classId);
  const base: BaseStats = { ...defaultBaseStats(), ...cls.baseStats(spec.level), ...spec.base, level: spec.level };
  const a = new Actor({
    id: spec.id ?? world.allocId(),
    kind: 'player',
    owner: spec.owner,
    team: spec.team ?? 'players',
    name: spec.name,
    pos: spec.pos,
    radius: 0.45,
    base,
  });
  a.look = spec.look ?? { model: cls.model, weapon: cls.weapon, tint: cls.hue };
  a.player = {
    classId: cls.id,
    level: spec.level,
    identity: { gauge: 0, max: cls.identity.gaugeMax, mode: 'idle', timer: 0, stacks: 0, counter: 0, cooldown: 0, data: {} },
    bar: spec.bar ?? [...cls.defaultBar],
    awakening: spec.awakening === undefined ? (cls.awakenings[0]?.id ?? null) : spec.awakening,
    dash: cls.dash.id,
    basic: cls.basic.id,
    skillLevels: { ...spec.skillLevels },
    tripods: { ...spec.tripods },
    standUpCd: 0,
    awakeningUses: spec.awakeningUses ?? 99,
    followUp: null,
    buffered: null,
    deaths: 0,
    seqUsed: 0,
    skillMods: { ...spec.skillMods },
    pvpMode: world.pvp,
  };
  a.passives = [...(spec.passives ?? [])];
  resolvePlayerSkills(a);
  cls.identity.init(a);
  world.addActor(a);
  return a;
}

/** (Re)builds the resolved skill table from the player's levels and facet picks. */
export function resolvePlayerSkills(a: Actor): void {
  const p = a.player;
  if (!p) return;
  const cls = registry.cls(p.classId);
  a.skills.clear();
  for (const def of cls.skills) {
    const lvl = p.skillLevels[def.id] ?? 1;
    a.skills.set(def.id, resolveSkill(def, lvl, p.tripods[def.id] ?? []));
  }
  // Identity skills, basics, dashes and awakenings are not levelled: they resolve at full power.
  for (const def of [...cls.identitySkills, cls.basic, cls.dash, ...cls.awakenings]) a.skills.set(def.id, resolveSkill(def, 10, p.tripods[def.id] ?? []));
  // Gems and passives adjust damage/cooldowns after tripods.
  for (const [id, s] of a.skills) {
    const m = p.skillMods[id];
    if (m?.dmg) s.dmgMult *= 1 + m.dmg;
    if (m?.cd) s.cooldown *= 1 - m.cd;
    for (const ref of a.passives) {
      const h = passiveHooks(ref);
      if (h?.cooldownMult) s.cooldown *= h.cooldownMult(s);
    }
  }
}

export interface MonsterSpawnOptions {
  id?: EntityId;
  owner?: PeerId;
  facing?: number;
  /** Elite variants in horde dungeons etc. */
  tags?: string[];
  hpMult?: number;
  team?: Team;
}

/** HP scaling for group content: bosses get tougher with more players. */
export function partyHpScale(partySize: number): number {
  return 1 + 0.75 * Math.max(0, partySize - 1);
}

export function createMonster(world: World, defId: string, level: number, pos: V2, opts: MonsterSpawnOptions = {}): Actor {
  const def = registry.monster(defId);
  const base: BaseStats = { ...defaultBaseStats(), ...def.stats(level), level };
  if (def.moveSpeed) base.moveSpeed = def.moveSpeed;
  const boss = isBossGrade(def.grade);
  const scale = GRADE_RANK[def.grade] >= GRADE_RANK.named ? partyHpScale(world.partySize) : 1;
  base.maxHp = Math.round(base.maxHp * scale * (opts.hpMult ?? 1));
  const a = new Actor({
    id: opts.id ?? world.allocId(),
    kind: 'monster',
    owner: opts.owner ?? world.hostPeer,
    team: opts.team ?? 'monsters',
    name: def.name,
    pos,
    facing: opts.facing ?? 0,
    radius: def.radius,
    base,
  });
  a.look = { model: def.model, scale: def.scale, tint: def.tint };
  const staggerMax = def.stagger ? def.stagger * world.partySize : 0;
  a.monster = {
    defId,
    grade: def.grade,
    level,
    home: { ...pos },
    leash: def.leash,
    aggroRange: def.aggroRange,
    targetId: null,
    threat: new Map(),
    stagger: staggerMax > 0 ? { max: staggerMax, value: staggerMax, active: true, recoverTimer: 0 } : null,
    parts: (def.parts ?? []).map((p) => ({ id: p.id, name: p.name, max: p.durability * world.partySize, value: p.durability * world.partySize, broken: false })),
    counterUntil: 0,
    countered: 0,
    hpBars: def.hpBars ?? (boss ? 30 : 1),
    weakness: def.weakness ?? null,
    // `params.immovable`: static objects (rift cores, boss illusions) ignore knockbacks and body pushes.
    immunePush: boss || def.params?.immovable === true,
    immuneCC: boss,
    lootTable: def.loot,
    xp: def.xp,
    brain: {},
    age: 0,
    idle: 0,
    tags: opts.tags ?? [],
  };
  for (const s of def.skills) a.skills.set(s.id, resolveSkill(s, 1, []));
  world.addActor(a);
  return a;
}
